// src/yomiri/tracking/services/AniListTracker.ts
import type { TrackerService, TrackRecord, TrackSearchItem, TrackerId, TrackingStatus } from '../types.ts';
import { delayedTrackingStore } from '../DelayedTrackingStore.ts';

const ANILIST_CLIENT_ID = import.meta.env?.VITE_ANILIST_CLIENT_ID || '';
const ANILIST_API_URL = 'https://graphql.anilist.co';
const STORAGE_KEY = 'yomiri_tracker_anilist_tokens';

export class AniListTracker implements TrackerService {
    readonly id: TrackerId = 'anilist';
    readonly name = 'AniList';
    readonly icon = 'https://anilist.co/img/icons/icon.svg';
    readonly supportsScore = true;
    readonly scoreFormat = 'POINT_10';

    private getTokens(): { accessToken: string; username?: string } | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private saveTokens(tokens: { accessToken: string; username?: string }) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
        window.dispatchEvent(new CustomEvent('yomiri-tracker-auth-changed', { detail: { trackerId: this.id } }));
    }

    isAuthenticated(): boolean {
        return !!this.getTokens()?.accessToken;
    }

    getUsername(): string | undefined {
        return this.getTokens()?.username;
    }

    getAuthUrl(): string {
        if (!ANILIST_CLIENT_ID) throw new Error('La autorización privada de AniList todavía no está configurada. Podés conectar tu lista pública desde Ajustes → Perfil y conexiones.');
        const redirectUri = window.location.origin + window.location.pathname;
        return `https://anilist.co/api/v2/oauth/authorize?client_id=${ANILIST_CLIENT_ID}&response_type=token`;
    }

    async handleAuthCallback(hashOrUrl: string): Promise<boolean> {
        // AniList devuelve el token en el fragmento hash: #access_token=...&token_type=Bearer&expires_in=...
        const hash = hashOrUrl.includes('#') ? hashOrUrl.split('#')[1] : hashOrUrl;
        const params = new URLSearchParams(hash);
        const accessToken = params.get('access_token');
        if (!accessToken) return false;

        // Consultar el nombre de usuario
        try {
            const res = await fetch(ANILIST_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    query: 'query { Viewer { id name avatar { medium } } }'
                })
            });
            const { data } = await res.json();
            const username = data?.Viewer?.name || 'AniList User';
            this.saveTokens({ accessToken, username });
            return true;
        } catch {
            this.saveTokens({ accessToken, username: 'AniList User' });
            return true;
        }
    }

    async logout(): Promise<void> {
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('yomiri-tracker-auth-changed', { detail: { trackerId: this.id } }));
    }

    async search(query: string): Promise<TrackSearchItem[]> {
        const gql = `
            query ($search: String) {
                Page(page: 1, perPage: 15) {
                    media(search: $search, type: MANGA) {
                        id
                        title {
                            romaji
                            english
                            userPreferred
                        }
                        coverImage {
                            large
                            medium
                        }
                        chapters
                        status
                        description
                        siteUrl
                    }
                }
            }
        `;
        const res = await fetch(ANILIST_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ query: gql, variables: { search: query } })
        });
        if (!res.ok) throw new Error(`AniList Search HTTP ${res.status}`);
        const { data } = await res.json();
        return (data?.Page?.media || []).map((m: any) => ({
            remoteId: String(m.id),
            title: m.title?.userPreferred || m.title?.english || m.title?.romaji || 'Sin título',
            coverUrl: m.coverImage?.large || m.coverImage?.medium,
            totalChapters: m.chapters || 0,
            publishingStatus: m.status,
            summary: m.description,
            remoteUrl: m.siteUrl || `https://anilist.co/manga/${m.id}`
        }));
    }

    async getEntry(remoteId: string): Promise<TrackRecord | null> {
        const tokens = this.getTokens();
        if (!tokens?.accessToken) return null;

        const gql = `
            query ($mediaId: Int) {
                MediaList(mediaId: $mediaId) {
                    id
                    status
                    score(format: POINT_10_DECIMAL)
                    progress
                }
                Media(id: $mediaId) {
                    chapters
                    siteUrl
                    title { userPreferred }
                }
            }
        `;
        try {
            const res = await fetch(ANILIST_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens.accessToken}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ query: gql, variables: { mediaId: Number(remoteId) } })
            });
            if (!res.ok) return null;
            const { data } = await res.json();
            const entry = data?.MediaList;
            const media = data?.Media;
            if (!entry) return null;

            return {
                mangaId: '',
                sourceId: '',
                trackerId: this.id,
                remoteId: String(remoteId),
                title: media?.title?.userPreferred || '',
                lastChapterRead: entry.progress || 0,
                totalChapters: media?.chapters || 0,
                score: entry.score || 0,
                status: this.mapAniListStatus(entry.status),
                remoteUrl: media?.siteUrl || `https://anilist.co/manga/${remoteId}`,
                updatedAt: Date.now()
            };
        } catch {
            return null;
        }
    }

    async updateEntry(record: TrackRecord, didReadChapter = false): Promise<TrackRecord> {
        const tokens = this.getTokens();
        if (!tokens?.accessToken) {
            throw new Error('No has iniciado sesión en AniList');
        }

        const gql = `
            mutation ($mediaId: Int, $progress: Int, $status: MediaListStatus, $score: Float) {
                SaveMediaListEntry(mediaId: $mediaId, progress: $progress, status: $status, score: $score) {
                    id
                    progress
                    status
                    score
                }
            }
        `;

        const variables = {
            mediaId: Number(record.remoteId),
            progress: Math.floor(record.lastChapterRead),
            status: this.mapStatusToAniList(record.status),
            score: record.score
        };

        const res = await fetch(ANILIST_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokens.accessToken}`,
                'Accept': 'application/json'
            },
            body: JSON.stringify({ query: gql, variables })
        });

        if (!res.ok) {
            throw new Error(`AniList sync error: HTTP ${res.status}`);
        }

        const updated: TrackRecord = {
            ...record,
            updatedAt: Date.now()
        };

        await delayedTrackingStore.saveTrackRecord(updated);
        return updated;
    }

    async bindManga(mangaId: string, sourceId: string, searchItem: TrackSearchItem): Promise<TrackRecord> {
        // Verificar si ya tiene progreso en la cuenta
        const existing = await this.getEntry(searchItem.remoteId);
        const record: TrackRecord = {
            mangaId,
            sourceId,
            trackerId: this.id,
            remoteId: searchItem.remoteId,
            title: searchItem.title,
            lastChapterRead: existing?.lastChapterRead || 0,
            totalChapters: searchItem.totalChapters || existing?.totalChapters || 0,
            score: existing?.score || 0,
            status: existing?.status || 'READING',
            remoteUrl: searchItem.remoteUrl,
            updatedAt: Date.now()
        };

        // Si el usuario no lo tenía en su lista de AniList, creamos la entrada inicial
        if (!existing && this.isAuthenticated()) {
            try {
                await this.updateEntry(record);
            } catch (err) {
                console.warn('[AniList] No se pudo crear entrada remota inicial:', err);
            }
        }

        await delayedTrackingStore.saveTrackRecord(record);
        return record;
    }

    async unbindManga(mangaId: string, trackerId: TrackerId): Promise<void> {
        await delayedTrackingStore.deleteTrackRecord(mangaId, trackerId);
    }

    private mapAniListStatus(status: string): TrackingStatus {
        switch (status) {
            case 'CURRENT': return 'READING';
            case 'COMPLETED': return 'COMPLETED';
            case 'PAUSED': return 'ON_HOLD';
            case 'DROPPED': return 'DROPPED';
            case 'PLANNING': return 'PLAN_TO_READ';
            case 'REPEATING': return 'REREADING';
            default: return 'READING';
        }
    }

    private mapStatusToAniList(status: TrackingStatus): string {
        switch (status) {
            case 'READING': return 'CURRENT';
            case 'COMPLETED': return 'COMPLETED';
            case 'ON_HOLD': return 'PAUSED';
            case 'DROPPED': return 'DROPPED';
            case 'PLAN_TO_READ': return 'PLANNING';
            case 'REREADING': return 'REPEATING';
            default: return 'CURRENT';
        }
    }
}

export const aniListTracker = new AniListTracker();
